import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "bootstrap-icons/font/bootstrap-icons.css";
import PdfViewerModal from "./PdfViewerModal";
import "./DynamicTable.css";
import { getFilePreviewUrl, getFileName } from "../utils/fileHelpers";

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const SortableRow = ({
  id,
  row = {},
  rowIndex = 0,
  handleInputChange = () => { },
  handleFileChange = () => { },
  handleRequiereArchivoChange = () => { },
  showButtons = false,
  addRow = () => { },
  onToggleActivo = () => { },
  data = [],
  tipo = "documento",
  tipoPersonaOptions = [],
  isInactiveTable = false,
  onPreviewFile = () => { },
}) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f8f9fa" : "inherit",
  };

  const shortenFileName = (fileName, maxLength = 20) => {
    if (!fileName) return "Seleccionar archivo";

    if (fileName instanceof File) {
      if (fileName.name.length <= maxLength) return fileName.name;
      const extension = fileName.name.split(".").pop();
      const nameWithoutExt = fileName.name.substring(
        0,
        fileName.name.length - extension.length - 1
      );
      const shortenedName =
        nameWithoutExt.substring(0, maxLength - extension.length - 3) + "...";
      return `${shortenedName}.${extension}`;
    }

    return getFileName({ ruta_archivo: fileName }) || "Seleccionar archivo";
  };

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        className={`${isDragging ? "dragging-row" : ""} ${!row.activo ? "table-secondary" : ""
          }`}
      >
        <td>{rowIndex + 1}</td>

        <td>
          <input
            type="text"
            className="form-control"
            value={row.titulo || ""}
            onChange={(e) =>
              handleInputChange(rowIndex, "titulo", e.target.value)
            }
            required
            disabled={isInactiveTable}
          />
        </td>

        <td>
          <textarea
            className="form-control"
            rows={1}
            value={row.descripcion || ""}
            onChange={(e) =>
              handleInputChange(rowIndex, "descripcion", e.target.value)
            }
            disabled={isInactiveTable}
          />
        </td>

        <td>
          <select
            className="form-select"
            value={row.tipo_persona || ""}
            onChange={(e) =>
              handleInputChange(rowIndex, "tipo_persona", e.target.value)
            }
            disabled={isInactiveTable}
          >
            {Array.isArray(tipoPersonaOptions) &&
              tipoPersonaOptions.map((option) => (
                <option key={option.value || ""} value={option.value || ""}>
                  {option.label || ""}
                </option>
              ))}
          </select>
        </td>

        {tipo === "certificado" && (
          <td className="text-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={row.requiere_archivo === true}
              onChange={(e) =>
                handleRequiereArchivoChange(rowIndex, e.target.checked)
              }
              disabled={isInactiveTable}
              style={{ transform: "scale(1.2)" }}
            />
          </td>
        )}

        <td>
          {tipo === "certificado" ? (
            <div className="d-flex align-items-center gap-2">
              <div className="input-group">
                <input
                  type="file"
                  accept=".pdf"
                  id={`file-input-${row.id}`}
                  className="form-control d-none"
                  onChange={(e) => {
                    handleFileChange(rowIndex, e.target.files[0]);
                    e.target.value = "";
                  }}
                  required={!row.ruta_archivo && !row.archivo}
                  disabled={isInactiveTable}
                />
                <label
                  htmlFor={`file-input-${row.id}`}
                  className={`btn ${isInactiveTable
                    ? "btn-outline-secondary disabled"
                    : "btn-outline-primary"
                    }`}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                  }}
                  title={row.archivo?.name || getFileName(row) || "Seleccionar archivo"}
                >
                  {shortenFileName(row.archivo?.name || row.ruta_archivo)}
                </label>
              </div>

              {(row.archivo || row.ruta_archivo) && (
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm"
                  onClick={() => onPreviewFile(row)}
                  title="Previsualizar archivo"
                >
                  <i className="bi bi-eye"></i>
                </button>
              )}

              {row.ruta_archivo && !row.archivo && (
                <span className="text-muted" title="Archivo ya cargado">
                  <i className="bi bi-check-circle-fill text-success"></i>
                </span>
              )}
            </div>
          ) : (
            <select
              className="form-select"
              value={row.obligatorio ? "obligatorio" : "opcional"}
              onChange={(e) =>
                handleInputChange(
                  rowIndex,
                  "obligatorio",
                  e.target.value === "obligatorio"
                )
              }
              disabled={isInactiveTable}
            >
              <option value="obligatorio">Obligatorio</option>
              <option value="opcional">Opcional</option>
            </select>
          )}
        </td>

        <td className="text-center">
          {isInactiveTable ? (
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => onToggleActivo(row.id)}
              title="Activar documento"
            >
              <i className="bi bi-check-lg"></i>
            </button>
          ) : showButtons ? (
            <>
              <button
                type="button"
                className="btn btn-success btn-sm me-1"
                onClick={addRow}
                title="Agregar fila"
              >
                <i className="bi bi-plus-lg"></i>
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => onToggleActivo(row.id, false)}
                title="Desactivar documento"
              >
                <i className="bi bi-dash-lg"></i>
              </button>
            </>
          ) : null}
        </td>
      </tr>
    </>
  );
};

const DynamicTable = ({
  data = [],
  setData = () => { },
  onSave = () => { },
  showButtons = false,
  editable = true,
  tipo = "documento",
  tipoPersonaFiltro = "todos",
  onToggleActivo = () => { },
  tipoPersonaOptions = [],
  isInactiveTable = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState({
    url: null,
    fileName: null,
  });

  const filteredData = data.filter((item) => {
    if (!tipoPersonaFiltro || tipoPersonaFiltro === "todos") return true;
    return item.tipo_persona === tipoPersonaFiltro;
  });

  const handleInputChange = (index, key, value) => {
    const updated = [...data];
    updated[index][key] = value;
    setData(updated);
  };

  const handleRequiereArchivoChange = (index, checked) => {
    const updated = [...data];
    updated[index]["requiere_archivo"] = checked;
    setData(updated);
  };

  const handleFileChange = (index, file) => {
    const updated = [...data];
    updated[index]["archivo"] = file;
    if (file) {
      updated[index]["ruta_archivo"] = null;
    }
    setData(updated);
  };

  const addRow = () => {
    const newRow = {
      id: `new-${Date.now()}`,
      titulo: "",
      descripcion: "",
      tipo_persona: "",
      obligatorio: true,
      requiere_archivo: false,
      orden:
        data.length > 0 ? Math.max(...data.map((d) => d.orden || 0)) + 1 : 1,
      activo: true,
      archivo: null,
      ruta_archivo: null,
      tipo: tipo,
    };
    setData([...data, newRow]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setData((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        return newItems.map((item, index) => ({
          ...item,
          orden: index + 1,
        }));
      });
    }
  };

  const handlePreviewFile = (row) => {
    const pdfUrl = getFilePreviewUrl(row);
    const fileName = getFileName(row);

    setPdfPreviewData({
      url: pdfUrl,
      fileName: fileName,
    });
    setShowPdfModal(true);
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfPreviewData.url && pdfPreviewData.url.startsWith("blob:")) {
      URL.revokeObjectURL(pdfPreviewData.url);
    }
  };

  return (
    <div className="mt-4">
      <div className="table-responsive">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="table table-bordered">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>N°</th>
                <th>Título</th>
                <th>Descripción</th>
                <th>Tipo de Persona</th>
                {tipo === "certificado" && (
                  <th style={{ width: "120px" }}>Requiere Archivo</th>
                )}
                <th>Archivo de la Política</th>
                <th style={{ width: "100px" }}>Acciones</th>
              </tr>
            </thead>
            <SortableContext
              items={filteredData.map((item) => ({ id: item.id }))}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {filteredData.map((row, rowIndex) => {
                  const originalIndex = data.findIndex(
                    (item) => item.id === row.id
                  );
                  return (
                    <SortableRow
                      key={row.id}
                      id={row.id}
                      row={row}
                      rowIndex={originalIndex}
                      handleInputChange={handleInputChange}
                      handleRequiereArchivoChange={handleRequiereArchivoChange}
                      handleFileChange={handleFileChange}
                      showButtons={showButtons}
                      addRow={addRow}
                      onToggleActivo={onToggleActivo}
                      data={data}
                      tipo={tipo}
                      tipoPersonaOptions={tipoPersonaOptions}
                      isInactiveTable={isInactiveTable}
                      onPreviewFile={handlePreviewFile}
                    />
                  );
                })}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>

        {showButtons && !isInactiveTable && (
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-success"
              onClick={onSave}
              disabled={data.some(
                (item) =>
                  !item.titulo ||
                  (tipo === "certificado" &&
                    !item.archivo &&
                    !item.ruta_archivo)
              )}
            >
              <i className="bi bi-save me-2"></i>Guardar Cambios
            </button>
          </div>
        )}
      </div>
      <PdfViewerModal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        pdfUrl={pdfPreviewData.url}
        fileName={pdfPreviewData.fileName}
        downloadUrl={pdfPreviewData.downloadUrl}
        rutaArchivo={pdfPreviewData.rutaArchivo}
      />
    </div>
  );
};

export default DynamicTable;